<template>
  <div class="home">

    <!-- Sticky Nav -->
    <nav :class="{ 'top_nav': true, 'active': showNav }">
      <div class="btns">
        <a class="btn" href="#plans">התחל עכשיו</a>
        <a class="login" :href="'https://dashboard.' + site.domain" target="_blank">התחברות</a>
      </div>
      <div class="logo"></div>
    </nav>

    <!-- HERO - מוקד לבעלי אתרים -->
    <section class="hero section-dark" ref="hero">
      <video ref="heroVideo" class="hero-video" autoplay muted loop playsinline></video>
      <div class="hero-video-overlay"></div>
      <div class="grid-pattern"></div>
      <div class="glow-orb green" style="width:500px;height:500px;top:-100px;inset-inline-end:-100px;"></div>
      <div class="glow-orb blue" style="width:400px;height:400px;bottom:-50px;inset-inline-start:-50px;"></div>
      <div class="hero-inner centered">
        <div class="hero-content hero-content-wide">
          <div class="hero-badge">
            <span class="badge badge-green"><span class="fas fa-bolt"></span> תשתית וידאו מהדור הבא</span>
          </div>
          <h1 class="hero-title">כל מה שצריך<br>כדי <span class="highlight">להזרים וידאו</span><br>באתר שלך</h1>
          <p class="hero-subtitle">העלה סרטונים בגרירה, הטמע נגן מעוצב באתר עם שורת קוד אחת. ממשק פשוט, בלי ידע טכני, והכל מוכן תוך דקות.</p>
          <div class="hero-ctas">
            <a href="#plans" class="btn">התחל עכשיו</a>
            <a href="#features" class="btn-outline"><span class="fas fa-info-circle"></span> למידע נוסף</a>
          </div>
          <div class="hero-stats">
            <div class="stat-item">
              <span class="stat-number">50M+</span>
              <span class="stat-label">צפיות בחודש</span>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
              <span class="stat-number">99.9%</span>
              <span class="stat-label">זמינות</span>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
              <span class="stat-number">4K</span>
              <span class="stat-label">איכות מקסימלית</span>
            </div>
          </div>
          <p class="hero-dev-hint">מפתחים? <router-link to="/docs" class="dev-docs-link">צפה בתיעוד API המלא <span class="fas fa-arrow-left"></span></router-link></p>
        </div>
      </div>
    </section>

    <!-- FOR WHO -->
    <section class="for-who section-light section-padding" ref="forWho">
      <div class="centered">
        <h2 class="section-title animate-in" :class="{ visible: sections.forWho }">בנוי עבורך</h2>
        <p class="section-subtitle animate-in animate-delay-1" :class="{ visible: sections.forWho }">בין אם אתה מנהל אתר תוכן או מפתח שבונה אפליקציה - WizzoStream עובד בשבילך</p>
        <div class="for-who-grid">
          <div class="for-who-card glass-card-light animate-in animate-delay-2" :class="{ visible: sections.forWho }">
            <div class="feature-icon"><span class="fas fa-globe"></span></div>
            <h3>בעלי אתרים</h3>
            <p class="card-desc">ממשק ניהול פשוט ואינטואיטיבי, בלי צורך בידע טכני</p>
            <ul class="for-who-features">
              <li><span class="fas fa-check"></span> העלאת סרטונים בגרירה</li>
              <li><span class="fas fa-check"></span> הטמעה בשורת קוד אחת</li>
              <li><span class="fas fa-check"></span> סטטיסטיקות ודשבורד</li>
              <li><span class="fas fa-check"></span> ניהול פרסומות וידאו</li>
            </ul>
            <a href="#plans" class="btn card-btn">התחל עכשיו</a>
          </div>
          <div class="for-who-card glass-card-light animate-in animate-delay-3" :class="{ visible: sections.forWho }">
            <div class="feature-icon blue"><span class="fas fa-code"></span></div>
            <h3>מפתחים</h3>
            <p class="card-desc">API מלא ומתועד, אינטגרציה תוך דקות עם כל מערכת</p>
            <ul class="for-who-features">
              <li><span class="fas fa-check"></span> REST API מלא</li>
              <li><span class="fas fa-check"></span> העלאת Chunks לקבצים גדולים</li>
              <li><span class="fas fa-check"></span> Webhook לעדכוני סטטוס</li>
              <li><span class="fas fa-check"></span> תיעוד מלא עם דוגמאות קוד</li>
            </ul>
            <router-link to="/docs" class="btn card-btn btn-dark">צפה בתיעוד</router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- FEATURES -->
    <section class="features section-dark section-padding" id="features" ref="features">
      <div class="grid-pattern"></div>
      <div class="centered">
        <h2 class="section-title animate-in" :class="{ visible: sections.features }">מה מיוחד ב-WizzoStream?</h2>
        <p class="section-subtitle animate-in animate-delay-1" :class="{ visible: sections.features }">טכנולוגיה מתקדמת שמכינה את הוידאו שלכם לצפייה מושלמת</p>
        <div class="features-grid">
          <div class="glass-card feature-card animate-in" :class="['animate-delay-' + (i+1), { visible: sections.features }]" v-for="(feature, i) in featureCards" :key="i">
            <div class="feature-icon" :class="feature.iconClass || ''"><span :class="feature.icon"></span></div>
            <h4>{{ feature.title }}</h4>
            <p>{{ feature.text }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- HOW IT WORKS -->
    <section class="how-it-works section-light section-padding" ref="howItWorks">
      <div class="centered">
        <h2 class="section-title animate-in" :class="{ visible: sections.howItWorks }">איך זה עובד?</h2>
        <p class="section-subtitle animate-in animate-delay-1" :class="{ visible: sections.howItWorks }">שלושה צעדים פשוטים ואתם בשידור חי</p>
        <div class="steps-grid">
          <div class="step-card animate-in animate-delay-2" :class="{ visible: sections.howItWorks }">
            <div class="step-number">1</div>
            <div class="step-icon"><span class="fas fa-user-plus"></span></div>
            <h4>הרשמה מיידית</h4>
            <p>צרו חשבון תוך שניות, בחרו מסלול ותקבלו גישה מיידית לדשבורד ול-API</p>
          </div>
          <div class="step-connector hide-mobile"><span class="fas fa-chevron-left"></span></div>
          <div class="step-card animate-in animate-delay-3" :class="{ visible: sections.howItWorks }">
            <div class="step-number">2</div>
            <div class="step-icon"><span class="fas fa-cloud-upload-alt"></span></div>
            <h4>העלאה ואינטגרציה</h4>
            <p>העלו סרטונים דרך הדשבורד או ה-API. הסרטונים מקודדים ומוכנים לצפייה אוטומטית</p>
          </div>
          <div class="step-connector hide-mobile"><span class="fas fa-chevron-left"></span></div>
          <div class="step-card animate-in animate-delay-4" :class="{ visible: sections.howItWorks }">
            <div class="step-number">3</div>
            <div class="step-icon"><span class="fas fa-play-circle"></span></div>
            <h4>הזרמה ברחבי העולם</h4>
            <p>הטמיעו את הנגן באתר שלכם עם שורת קוד אחת. CDN גלובלי מהיר</p>
          </div>
        </div>
      </div>
    </section>

    <!-- API PREVIEW - למטה עם הזמנה לתיעוד מלא -->
    <section class="api-preview section-navy section-padding" ref="apiPreview" id="api-docs">
      <div class="centered">
        <div class="api-preview-layout">
          <div class="api-preview-text animate-in" :class="{ visible: sections.apiPreview }">
            <span class="badge badge-blue"><span class="fas fa-plug"></span> למפתחים</span>
            <h2 class="section-title">התממשקות<br>בשורות קוד בודדות</h2>
            <p class="section-subtitle">REST API מלא להעלאה, ניהול והזרמת וידאו. תיעוד מלא עם דוגמאות ב-JavaScript, PHP, Python ו-cURL.</p>
            <router-link to="/docs" class="btn api-docs-cta"><span class="fas fa-book"></span> תיעוד API המלא <span class="fas fa-arrow-left"></span></router-link>
          </div>
          <div class="api-preview-code animate-in animate-delay-2" :class="{ visible: sections.apiPreview }">
            <div class="code-window">
              <div class="code-window-bar">
                <span class="dot red"></span>
                <span class="dot yellow"></span>
                <span class="dot green"></span>
                <div class="code-tabs">
                  <span class="code-tab" :class="{ active: activeTab === 'js' }" @click="activeTab = 'js'">JavaScript</span>
                  <span class="code-tab" :class="{ active: activeTab === 'php' }" @click="activeTab = 'php'">PHP</span>
                  <span class="code-tab" :class="{ active: activeTab === 'python' }" @click="activeTab = 'python'">Python</span>
                  <span class="code-tab" :class="{ active: activeTab === 'curl' }" @click="activeTab = 'curl'">cURL</span>
                </div>
              </div>
              <div class="code-block" v-show="activeTab === 'js'" v-html="codeExamples.js"></div>
              <div class="code-block" v-show="activeTab === 'php'" v-html="codeExamples.php"></div>
              <div class="code-block" v-show="activeTab === 'python'" v-html="codeExamples.python"></div>
              <div class="code-block" v-show="activeTab === 'curl'" v-html="codeExamples.curl"></div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- PLANS -->
    <section class="plans-section section-dark section-padding" id="plans" ref="plans">
      <div class="grid-pattern"></div>
      <div class="centered">
        <h2 class="section-title animate-in" :class="{ visible: sections.plans }">המסלולים שלנו</h2>
        <p class="section-subtitle animate-in animate-delay-1" :class="{ visible: sections.plans }">תמחור פשוט וברור, בלי הפתעות</p>
        <div class="plans-grid">
          <div class="plan-card animate-in" :class="['animate-delay-' + (i+2), { visible: sections.plans, popular: i === 1 }]" v-for="(plan, i) in plans" :key="plan.id">
            <div class="plan-popular-badge" v-if="i === 1">פופולרי</div>
            <h3 class="plan-name" v-text="plan.name"></h3>
            <div class="plan-price">
              <span class="price-amount" v-text="plan.price_with_vat || plan.price"></span>
              <span class="price-currency">₪</span>
              <span class="price-period" v-text="plan.text_ander_praice"></span>
              <span class="price-vat">כולל מע"מ</span>
            </div>
            <router-link :to="{ name: 'plan', params: { id: plan.id }}" class="btn plan-cta" :class="{ 'btn-outline': i !== 1 }">הרשמה עכשיו</router-link>
            <ul class="plan-features">
              <li v-if="plan.text_field_a"><span class="fas fa-check"></span> {{ plan.text_field_a }}</li>
              <li v-if="plan.text_field_b"><span class="fas fa-check"></span> {{ plan.text_field_b }}</li>
              <li v-if="plan.text_field_c"><span class="fas fa-check"></span> {{ plan.text_field_c }}</li>
              <li v-if="plan.text_field_d"><span class="fas fa-check"></span> {{ plan.text_field_d }}</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- CONTACT / CTA -->
    <section id="contact" class="contact-section section-padding" ref="contact">
      <div class="centered">
        <div class="contact-layout">
          <div class="contact-text animate-in" :class="{ visible: sections.contact }">
            <h2 class="section-title">יש שאלות?<br>דברו איתנו</h2>
            <p class="section-subtitle">נשמח לעזור לכם להתחיל. צוות התמיכה שלנו כאן עבורכם.</p>
            <div class="contact-info">
              <a class="contact-item" href="mailto:support@wizzo.stream"><span class="fas fa-envelope"></span> support@wizzo.stream</a>
              <a class="contact-item" href="tel:+972527752229"><span class="fas fa-phone"></span> <span dir="ltr">052-775-2229</span></a>
            </div>
          </div>
          <form @submit.prevent="addLead" class="contact-form animate-in animate-delay-2" :class="{ visible: sections.contact }">
            <div class="form-row">
              <input type="text" v-model="leadForm.name" placeholder="שם מלא" required>
              <input type="email" v-model="leadForm.email" placeholder="כתובת מייל" required>
            </div>
            <div class="form-field">
              <input type="tel" inputmode="tel" autocomplete="tel" dir="ltr" ref="phone" v-model="leadForm.phone" placeholder="מספר נייד" required
                :class="{ 'is-invalid': phoneError }" :aria-invalid="phoneError ? 'true' : null" :aria-describedby="phoneError ? 'lead_phone_err' : null"
                @input="phoneError = ''" @invalid.prevent="phoneInvalid">
              <p v-if="phoneError" id="lead_phone_err" class="field-error" role="alert" v-text="phoneError"></p>
            </div>
            <textarea rows="5" v-model="leadForm.content" placeholder="איך נוכל לעזור?"></textarea>
            <p v-if="sendError" class="field-error" role="alert" v-text="sendError"></p>
            <button type="submit" class="btn form-submit">שליחה</button>
          </form>
        </div>
      </div>
    </section>

  </div>
</template>

<script>
import { checkMobile, PHONE_MSG } from '@/phone';

export default {
  props: ['site', 'plans', 'cdn_domain'],
  data() {
    return {
      leadForm: { name: '', email: '', phone: '', content: '' },
      phoneError: '',
      sendError: '',
      sending: false,
      showNav: false,
      activeTab: 'js',
      sections: {
        forWho: false,
        features: false,
        apiPreview: false,
        howItWorks: false,
        plans: false,
        contact: false,
      },
      featureCards: [
        { icon: 'fas fa-bolt', title: 'קידוד מהיר במיוחד', text: 'טכנולוגיית קידוד מתקדמת שמכינה את הוידאו להזרמה תוך שניות ספורות.' },
        { icon: 'fas fa-mobile-alt', title: 'סטרימינג אדפטיבי', text: 'התאמה אוטומטית של איכות הוידאו לרוחב הפס ולגודל המסך של הצופה.', iconClass: 'blue' },
        { icon: 'fas fa-eye-slash', title: 'ללא מיתוג', text: 'חווית צפייה נקייה לחלוטין. ללא לוגו, סימני מים או פרסומות שלנו.' },
        { icon: 'fas fa-photo-video', title: 'איכויות עד 4K', text: 'תמיכה במגוון רחב של איכויות מהסטנדרט הנמוך ועד 4K מלא.', iconClass: 'purple' },
        { icon: 'fas fa-globe', title: 'CDN גלובלי', text: 'רשת הפצה מהירה עם נקודות נוכחות ברחבי העולם לצפייה חלקה.', iconClass: 'blue' },
        { icon: 'fas fa-code', title: 'API מלא', text: 'REST API מתועד עם תמיכה בהעלאה, ניהול, מחיקה ושליפת סרטונים.' },
      ],
      codeExamples: {
        js: `<span class="token-comment">// העלאת סרטון עם JavaScript</span>
<span class="token-keyword">const</span> <span class="token-variable">formData</span> = <span class="token-keyword">new</span> <span class="token-function">FormData</span>();
<span class="token-variable">formData</span>.<span class="token-method">append</span>(<span class="token-string">'file'</span>, <span class="token-variable">videoFile</span>);
<span class="token-variable">formData</span>.<span class="token-method">append</span>(<span class="token-string">'title'</span>, <span class="token-string">'הסרטון שלי'</span>);

<span class="token-keyword">const</span> <span class="token-variable">res</span> = <span class="token-keyword">await</span> <span class="token-function">fetch</span>(
  <span class="token-string">'https://api.wizzo.stream/api_wizzocms/video_manager/upload'</span>,
  {
    <span class="token-property">method</span>: <span class="token-string">'POST'</span>,
    <span class="token-property">headers</span>: {
      <span class="token-string">'Authorization'</span>: <span class="token-string">\`Bearer \${API_KEY}\`</span>
    },
    <span class="token-property">body</span>: <span class="token-variable">formData</span>
  }
);
<span class="token-keyword">const</span> <span class="token-variable">data</span> = <span class="token-keyword">await</span> <span class="token-variable">res</span>.<span class="token-method">json</span>();`,

        php: `<span class="token-comment">// העלאת סרטון עם PHP</span>
<span class="token-variable">$curl</span> = <span class="token-function">curl_init</span>();

<span class="token-function">curl_setopt_array</span>(<span class="token-variable">$curl</span>, [
  <span class="token-property">CURLOPT_URL</span> => <span class="token-string">'https://api.wizzo.stream/api_wizzocms/video_manager/upload'</span>,
  <span class="token-property">CURLOPT_POST</span> => <span class="token-keyword">true</span>,
  <span class="token-property">CURLOPT_RETURNTRANSFER</span> => <span class="token-keyword">true</span>,
  <span class="token-property">CURLOPT_HTTPHEADER</span> => [
    <span class="token-string">'Authorization: Bearer '</span> . <span class="token-variable">$apiKey</span>
  ],
  <span class="token-property">CURLOPT_POSTFIELDS</span> => [
    <span class="token-string">'file'</span> => <span class="token-keyword">new</span> <span class="token-function">CURLFile</span>(<span class="token-variable">$filePath</span>),
    <span class="token-string">'title'</span> => <span class="token-string">'הסרטון שלי'</span>
  ]
]);

<span class="token-variable">$response</span> = <span class="token-function">curl_exec</span>(<span class="token-variable">$curl</span>);
<span class="token-variable">$data</span> = <span class="token-function">json_decode</span>(<span class="token-variable">$response</span>, <span class="token-keyword">true</span>);`,

        python: `<span class="token-comment"># העלאת סרטון עם Python</span>
<span class="token-keyword">import</span> <span class="token-variable">requests</span>

<span class="token-variable">url</span> = <span class="token-string">'https://api.wizzo.stream/api_wizzocms/video_manager/upload'</span>
<span class="token-variable">headers</span> = {
  <span class="token-string">'Authorization'</span>: <span class="token-string">f'Bearer {<span class="token-variable">api_key</span>}'</span>
}
<span class="token-variable">files</span> = {
  <span class="token-string">'file'</span>: <span class="token-function">open</span>(<span class="token-string">'video.mp4'</span>, <span class="token-string">'rb'</span>)
}
<span class="token-variable">data</span> = {
  <span class="token-string">'title'</span>: <span class="token-string">'הסרטון שלי'</span>
}

<span class="token-variable">response</span> = <span class="token-variable">requests</span>.<span class="token-method">post</span>(
  <span class="token-variable">url</span>,
  <span class="token-property">headers</span>=<span class="token-variable">headers</span>,
  <span class="token-property">files</span>=<span class="token-variable">files</span>,
  <span class="token-property">data</span>=<span class="token-variable">data</span>
)
<span class="token-variable">result</span> = <span class="token-variable">response</span>.<span class="token-method">json</span>()`,

        curl: `<span class="token-comment"># העלאת סרטון עם cURL</span>
<span class="token-function">curl</span> -X POST \\
  <span class="token-string">'https://api.wizzo.stream/api_wizzocms/video_manager/upload'</span> \\
  -H <span class="token-string">'Authorization: Bearer YOUR_API_KEY'</span> \\
  -F <span class="token-string">'file=@video.mp4'</span> \\
  -F <span class="token-string">'title=הסרטון שלי'</span>`
      }
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
    this.handleScroll();
    this.initHeroVideo();
    // a link that arrives with a hash (the accessibility panel's "contact" link opens
    // /#contact) lands on its section: the router's own scroll ran before this page existed
    this.$nextTick(() => {
      const el = this.$route.hash && document.querySelector(this.$route.hash);
      if (el) el.scrollIntoView();
    });
  },
  unmounted() {
    window.removeEventListener('scroll', this.handleScroll);
    if (this._heroHls) {
      this._heroHls.destroy();
    }
  },
  methods: {
    handleScroll() {
      const scrollY = window.scrollY;
      this.showNav = scrollY > (this.is_mobile() ? 80 : 200);

      const refs = {
        forWho: this.$refs.forWho,
        features: this.$refs.features,
        apiPreview: this.$refs.apiPreview,
        howItWorks: this.$refs.howItWorks,
        plans: this.$refs.plans,
        contact: this.$refs.contact,
      };
      const windowBottom = scrollY + window.innerHeight;
      for (const key in refs) {
        if (refs[key] && !this.sections[key]) {
          const offset = refs[key].offsetTop + 100;
          if (windowBottom > offset) this.sections[key] = true;
        }
      }
    },
    // the browser's own "required" bubble is replaced by the same message under the field
    phoneInvalid() {
      this.phoneError = PHONE_MSG[checkMobile(this.leadForm.phone).reason || 'missing'];
    },
    addLead() {
      if (this.sending) return;
      this.sendError = '';

      // a bad or missing mobile sends nothing; the server checks it again
      const phone = checkMobile(this.leadForm.phone);
      if (!phone.ok) {
        this.phoneError = PHONE_MSG[phone.reason];
        this.$refs.phone.focus();
        return;
      }
      this.leadForm.phone = phone.phone;

      const failed = () => {
        this.sending = false;
        this.sendError = 'ההודעה לא נשלחה. נסו שוב בעוד רגע, או כתבו לנו ל-support@wizzo.stream';
      };
      this.sending = true;
      this.api({ action: 'contact/add', data: this.leadForm }, (data) => {
        if (data && data.data) {
          this.sending = false;
          this.leadForm = { name: '', email: '', phone: '', content: '' };
          this.toast('הודעתך נשלחה בהצלחה!');
        } else failed();
      }, (err) => {
        const body = err && err.response && err.response.data;
        if (body && body.field === 'phone') {
          this.sending = false;
          this.phoneError = body.msg || PHONE_MSG.invalid;
          this.$refs.phone.focus();
        } else failed();
      });
    },
    initHeroVideo() {
      const video = this.$refs.heroVideo;
      if (!video) return;
      const hlsUrl = 'https://matkon.wizzo.stream/storage/2026/03/ckt6nfcv/master.m3u8';
      if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = hlsUrl;
        video.play().catch(() => {});
      } else if (window.Hls && window.Hls.isSupported()) {
        const hls = new window.Hls();
        hls.loadSource(hlsUrl);
        hls.attachMedia(video);
        hls.on(window.Hls.Events.MANIFEST_PARSED, () => {
          video.play().catch(() => {});
        });
        this._heroHls = hls;
      }
    }
  },
}
</script>

<style lang="scss">
.home { display: flex; flex-direction: column; }

/* ─── HERO ─── */
.hero { position: relative; overflow: hidden; min-height: 100vh; display: flex; align-items: center;
  @media (max-width: 768px) { min-height: auto; padding: 100px 20px 60px; }
  @media (min-width: 769px) { padding: 0 40px; }
}
.hero-video { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; z-index: 0; }
.hero-video-overlay { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(10, 15, 30, 0.85) 0%, rgba(10, 15, 30, 0.75) 50%, rgba(10, 15, 30, 0.9) 100%); z-index: 0; pointer-events: none; }
.hero-inner { display: flex; align-items: center; gap: 60px; position: relative; z-index: 1;
  @media (max-width: 768px) { flex-direction: column; gap: 40px; }
}
.hero-content { flex: 1;
  &.hero-content-wide { max-width: 720px; margin: 0 auto; }
  .hero-dev-hint { margin-top: 32px; font-size: var(--h6); color: var(--text-muted); }
  .dev-docs-link { color: var(--color1); margin-inline-start: 6px; }
  .hero-badge { margin-bottom: 24px; }
  .hero-title { font-size: var(--h1); font-weight: 800; color: #fff; line-height: 1.15; margin-bottom: 20px;
    .highlight { color: var(--color1); position: relative; }
  }
  .hero-subtitle { font-size: var(--h4); color: var(--text-muted); line-height: 1.7; margin-bottom: 36px; }
  .hero-ctas { display: flex; gap: 16px; margin-bottom: 48px;
    @media (max-width: 768px) { flex-direction: column; }
    .btn, .btn-outline { display: flex; align-items: center; gap: 8px; justify-content: center; }
  }
  .hero-stats { display: flex; gap: 40px;
    @media (max-width: 768px) { gap: 20px; justify-content: center; }
    .stat-divider { width: 1px; background: var(--slate); }
  }
}
.code-window { border-radius: var(--radius_s); overflow: hidden; border: 1px solid var(--code-border); box-shadow: 0 20px 60px rgba(0,0,0,0.4);
  .code-window-bar { display: flex; align-items: center; gap: 8px; padding: 12px 16px; background: #161B22; border-bottom: 1px solid var(--code-border);
    .dot { width: 12px; height: 12px; border-radius: 50%;
      &.red { background: #FF5F56; }
      &.yellow { background: #FFBD2E; }
      &.green { background: #27C93F; }
    }
    .code-window-title { font-family: var(--font-mono); font-size: 12px; color: var(--text-muted); margin-inline-start: auto; margin-inline-end: 16px; }
    // .code-tabs is direction: ltr, so its inline-end is the side facing the dots
    .code-tabs { margin-inline-end: auto; min-width: 0; overflow-x: auto; }
    // a phone has room for the four tabs only without the decorative dots
    @media (max-width: 480px) {
      .dot { display: none; }
      .code-tabs { margin-inline-end: 0; }
      .code-tab { padding-inline: 12px; }
    }
  }
  .code-block { margin: 0; border: none; border-radius: 0; max-height: 400px; overflow-y: auto; }
}

/* ─── SECTIONS COMMON ─── */
.section-title { font-size: var(--h2); font-weight: 800; text-align: center; margin-bottom: 12px; }
.section-dark .section-title { color: #fff; }
.section-light .section-title, .section-white .section-title { color: var(--text-dark); }
.section-subtitle { font-size: var(--h4); text-align: center; color: var(--text-muted); max-width: 600px; margin: 0 auto 50px; line-height: 1.6; }

/* ─── FOR WHO ─── */
.for-who-grid { display: grid; gap: 30px;
  @media (min-width: 769px) { grid-template-columns: 1fr 1fr; }
}
.for-who-card { text-align: center; padding: 48px 36px; display: flex; flex-direction: column; align-items: center; gap: 16px;
  h3 { font-size: var(--h3); font-weight: 700; color: var(--text-dark); }
  .card-desc { font-size: var(--h5); color: var(--text-mid); line-height: 1.6; }
  .for-who-features { text-align: start; display: flex; flex-direction: column; gap: 12px; width: 100%; max-width: 280px; margin: 8px 0;
    li { display: flex; align-items: center; gap: 10px; font-size: var(--h5); color: var(--text-mid);
      .fa-check { color: var(--color1); font-size: 14px; }
    }
  }
  .card-btn { margin-top: auto; width: 100%; max-width: 240px; text-align: center; justify-content: center; }
  .btn-dark { background: var(--navy-mid); color: #fff;
    &:hover { background: var(--navy-deep); }
  }
}

/* ─── FEATURES ─── */
.features { position: relative; overflow: hidden; }
.features-grid { display: grid; gap: 20px;
  @media (min-width: 769px) { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 768px) { grid-template-columns: 1fr; }
}
.feature-card { display: flex; flex-direction: column; gap: 14px;
  h4 { font-size: var(--h4); font-weight: 700; color: #fff; }
  p { font-size: var(--h5); color: var(--text-muted); line-height: 1.6; }
}

/* ─── API PREVIEW ─── */
.api-preview { background: var(--navy-mid); }
.api-preview-layout { display: flex; gap: 50px; align-items: flex-start;
  // stretch on mobile: a column of flex-start items takes the code's widest line and pushed the page sideways
  @media (max-width: 768px) { flex-direction: column; align-items: stretch; }
}
.api-preview-text { flex: 1;
  .section-title { text-align: start; }
  .section-subtitle { text-align: start; margin: 0 0 24px; max-width: none; }
  .badge { margin-bottom: 16px; }
  .api-docs-cta { display: inline-flex; align-items: center; gap: 10px; }
}
.api-endpoints { display: flex; flex-direction: column; gap: 12px; margin-bottom: 8px;
  .endpoint-item { display: flex; align-items: center; gap: 12px; padding: 10px 16px; background: rgba(255,255,255,0.04); border-radius: var(--radius_xs); border: 1px solid rgba(255,255,255,0.06);
    code { font-family: var(--font-mono); font-size: 14px; color: var(--text-light); }
  }
}
.api-preview-code { flex: 1.2; min-width: 0; }

/* ─── HOW IT WORKS ─── */
.steps-grid { display: flex; align-items: center; justify-content: center; gap: 16px;
  @media (max-width: 768px) { flex-direction: column; gap: 24px; }
}
.step-card { flex: 1; max-width: 320px; background: #fff; border-radius: var(--radius_s); padding: 36px 28px; text-align: center; position: relative; border: 1px solid var(--surface-dark); transition: all 0.3s ease;
  &:hover { transform: translateY(-6px); box-shadow: var(--shadow-md); border-color: var(--color1); }
  .step-number { position: absolute; top: -16px; inset-inline-start: 24px; width: 32px; height: 32px; background: var(--color1); color: var(--navy-deep); border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 14px; }
  .step-icon { width: 64px; height: 64px; margin: 0 auto 20px; background: var(--color1-dim); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: var(--color1); font-size: 24px; }
  h4 { font-size: var(--h4); font-weight: 700; color: var(--text-dark); margin-bottom: 10px; }
  p { font-size: var(--h6); color: var(--text-mid); line-height: 1.6; }
}
.step-connector { color: var(--surface-dark); font-size: 20px; }

/* ─── PLANS ─── */
.plans-section { position: relative; overflow: hidden; }
.plans-grid { display: grid; gap: 24px;
  @media (min-width: 769px) { grid-template-columns: repeat(3, 1fr); }
}
.plan-card { background: rgba(255,255,255,0.04); backdrop-filter: blur(10px); border: 1px solid rgba(255,255,255,0.08); border-radius: var(--radius_s); padding: 40px 32px; display: flex; flex-direction: column; align-items: center; text-align: center; position: relative; transition: all 0.3s ease;
  &:hover { transform: translateY(-6px); border-color: rgba(46,247,186,0.2); }
  &.popular { border-color: var(--color1); background: rgba(46,247,186,0.05);
    .plan-cta { background: var(--color1); color: var(--navy-deep); }
  }
  .plan-popular-badge { position: absolute; top: -12px; background: var(--color1); color: var(--navy-deep); padding: 4px 20px; border-radius: 100px; font-size: 13px; font-weight: 700; }
  .plan-name { font-size: var(--h3); font-weight: 700; color: #fff; margin-bottom: 20px; }
  .plan-price { display: flex; align-items: baseline; gap: 4px; margin-bottom: 8px;
    .price-amount { font-size: 64px; font-weight: 800; color: var(--color1); }
    .price-currency { font-size: 24px; color: var(--color1); }
    .price-period { font-size: var(--h6); color: var(--text-muted); margin-inline-start: 8px; }
    .price-vat { font-size: 12px; color: var(--text-muted); margin-inline-start: 6px; }
  }
  .plan-cta { width: 100%; text-align: center; justify-content: center; margin: 20px 0 28px; padding: 14px; }
  .plan-features { display: flex; flex-direction: column; gap: 14px; width: 100%; text-align: start;
    li { display: flex; align-items: center; gap: 10px; font-size: var(--h5); color: var(--text-light);
      .fa-check { color: var(--color1); font-size: 14px; flex-shrink: 0; }
    }
  }
}

/* ─── CONTACT ─── */
.contact-section { background: linear-gradient(135deg, var(--navy-mid) 0%, var(--navy-deep) 100%); color: var(--text-light);
  // /#contact lands below the fixed nav, or the phone's title hides under it
  scroll-margin-top: 72px; }
.contact-layout { display: flex; gap: 60px; align-items: flex-start;
  // stretch, or the form shrinks to half the phone's width
  @media (max-width: 768px) { flex-direction: column; gap: 40px; align-items: stretch; }
}
.contact-text { flex: 1;
  .section-title { text-align: start; color: #fff; }
  .section-subtitle { text-align: start; margin: 12px 0 30px; max-width: none; }
}
.contact-info { display: flex; flex-direction: column; gap: 14px;
  .contact-item { display: flex; align-items: center; gap: 12px; font-size: var(--h5); color: var(--text-muted); width: fit-content;
    .fas { color: var(--color1); width: 20px; text-align: center; }
    &:hover { color: var(--color1); }
  }
}
.contact-form { flex: 1; display: flex; flex-direction: column; gap: 16px;
  .form-row { display: flex; gap: 16px;
    @media (max-width: 768px) { flex-direction: column; }
    input { flex: 1; }
  }
  // the general input rule (white field, 0,2,1) outranked a plain "input" here: white text on white, nothing typed was visible
  input:not([type=checkbox]):not([type=submit]), textarea { background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1); color: #fff; padding: 14px 18px; border-radius: var(--radius_xs); font-size: var(--h5);
    &::placeholder { color: var(--text-muted); }
    &:focus { border-color: var(--color1); background: rgba(255,255,255,0.08); box-shadow: 0 0 0 3px var(--color1-dim); }
  }
  .form-field { display: flex; flex-direction: column; gap: 8px; }
  // the general input rule (0,2,1) sets the border colours: the wrong-field mark outranks it
  input.is-invalid:not([type=checkbox]):not([type=submit]) { border-color: #FF7B72;
    &:focus { border-color: #FF7B72; box-shadow: 0 0 0 3px rgba(255,123,114,0.2); }
  }
  .field-error { margin: 0; color: #FF7B72; font-size: var(--h6); line-height: 1.5; text-align: start; }
  .form-submit { align-self: flex-start; padding: 14px 40px; border: none; cursor: pointer;
    @media (max-width: 768px) { width: 100%; }
  }
}
</style>
